/**
 * App Version Utility
 * Handles app version detection from React Native WebView
 */

class AppVersionManager {
    constructor() {
        this.appVersion = null;
        this.appBuildNumber = null;
        this.appPlatform = null;
        this.deviceColorScheme = null;
        this.isInApp = false;

        this.init();
    }

    init() {
        // Check if we're running in the app
        this.detectAppEnvironment();

        // Listen for app version events
        this.setupEventListeners();

        // Request app version if we're in the app
        if (this.isInApp) {
            this.requestAppVersion();
        }
    }

    detectAppEnvironment() {
        // Check if we're running in a React Native WebView
        this.isInApp = !!(
            window.ReactNativeWebView ||
            window.webkit?.messageHandlers?.ReactNativeWebView ||
            navigator.userAgent.includes('ReactNativeWebView')
        );
    }

    setupEventListeners() {
        // Listen for app version received event
        window.addEventListener('appVersionReceived', event => {
            const { version, buildNumber, platform, colorScheme } =
                event.detail;
            this.appVersion = version;
            this.appBuildNumber = buildNumber;
            this.appPlatform = platform;
            this.deviceColorScheme = colorScheme;

            console.log('App version received:', {
                version,
                buildNumber,
                platform,
                colorScheme,
            });

            // Dispatch custom event for components to listen to
            window.dispatchEvent(
                new CustomEvent('appVersionUpdated', {
                    detail: { version, buildNumber, platform, colorScheme },
                })
            );
        });

        // Listen for message events from React Native
        window.addEventListener('message', event => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'appVersionInfo') {
                    this.appVersion = data.version;
                    this.appBuildNumber = data.buildNumber;
                    this.appPlatform = data.platform;
                    this.deviceColorScheme = data.colorScheme;

                    console.log('App version info received:', data);

                    // Dispatch custom event for components to listen to
                    window.dispatchEvent(
                        new CustomEvent('appVersionUpdated', {
                            detail: {
                                version: data.version,
                                buildNumber: data.buildNumber,
                                platform: data.platform,
                                colorScheme: data.colorScheme,
                            },
                        })
                    );
                }
            } catch (error) {
                // Ignore non-JSON messages
            }
        });
    }

    requestAppVersion() {
        if (this.isInApp && window.ReactNativeWebView) {
            // Send message to React Native to request app version
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: 'getAppVersion',
                })
            );
        }
    }

    getVersion() {
        return this.appVersion;
    }

    getBuildNumber() {
        return this.appBuildNumber;
    }

    getPlatform() {
        return this.appPlatform;
    }

    getDeviceColorScheme() {
        return this.deviceColorScheme;
    }

    isInAppEnvironment() {
        return this.isInApp;
    }

    getFormattedVersion() {
        if (!this.appVersion) {
            return null;
        }

        let formatted = `ver. ${this.appVersion}`;

        return formatted;
    }

    // Wait for app version to be available
    waitForVersion(timeout = 5000) {
        return new Promise((resolve, reject) => {
            if (this.appVersion) {
                resolve({
                    version: this.appVersion,
                    buildNumber: this.appBuildNumber,
                    platform: this.appPlatform,
                    colorScheme: this.deviceColorScheme,
                });
                return;
            }

            const timeoutId = setTimeout(() => {
                window.removeEventListener('appVersionUpdated', handler);
                reject(new Error('App version request timeout'));
            }, timeout);

            const handler = event => {
                clearTimeout(timeoutId);
                window.removeEventListener('appVersionUpdated', handler);
                resolve(event.detail);
            };

            window.addEventListener('appVersionUpdated', handler);
        });
    }
}

// Create singleton instance
const appVersionManager = new AppVersionManager();

export default appVersionManager;
